import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { brand } from '../../theme';
import { TRANSCRIPT_SHORTCUTS } from '../Transcript/transcriptMarking';

export interface ShortcutGroup {
  title: string;
  items: Array<{ keys: string[]; label: string }>;
}

export const STUDIO_SHORTCUTS: ShortcutGroup[] = [
  {
    title: 'Playback',
    items: [
      { keys: ['Space'], label: 'Play / pause' },
      { keys: ['J', 'K', 'L'], label: 'Shuttle back / stop / forward (press again to speed up)' },
      { keys: ['←', '→'], label: 'Back / forward 5 seconds' },
      { keys: ['Shift', '← →'], label: 'Back / forward 10 seconds' },
      { keys: [',', '.'], label: 'Previous / next frame' },
      { keys: ['Home', 'End'], label: 'Start / end of the video' },
      { keys: ['↑', '↓'], label: 'Volume up / down' },
      { keys: ['M'], label: 'Mute' },
      { keys: ['F'], label: 'Full screen' },
    ],
  },
  {
    title: 'Notes',
    items: [
      { keys: ['A'], label: 'Add a note at the playhead' },
      { keys: ['Insert'], label: 'Add a note at the playhead' },
      { keys: ['I', 'O'], label: 'Mark In / Out for a range note' },
      { keys: ['Shift', 'L'], label: 'Loop between In and Out' },
      { keys: ['Esc'], label: 'Clear In / Out' },
      { keys: ['[', ']'], label: 'Previous / next note' },
      { keys: ['Enter'], label: 'Add the note you are typing (Shift + Enter for a new line)' },
    ],
  },
  {
    title: 'Timeline',
    items: [
      { keys: ['+', '−'], label: 'Zoom in / out' },
      { keys: ['\\'], label: 'Fit the whole video' },
      { keys: ['Ctrl / ⌘', 'scroll'], label: 'Zoom around the pointer' },
      { keys: ['Alt', '← →'], label: 'Nudge the focused marker one frame' },
      { keys: ['?'], label: 'Show these shortcuts' },
    ],
  },
  TRANSCRIPT_SHORTCUTS,
];

const Key: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 24,
      height: 24,
      px: 0.75,
      borderRadius: 1.5,
      border: `1px solid ${brand.borderStrong}`,
      borderBottomWidth: 2,
      bgcolor: brand.ivory,
      fontFamily: 'inherit',
      fontSize: 12,
      fontWeight: 700,
      color: 'text.primary',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
);

interface ShortcutHelpDialogProps {
  open: boolean;
  onClose: () => void;
  groups?: ShortcutGroup[];
  /** The dialog's title; "Keyboard shortcuts" unless there's an intro. */
  title?: string;
  /** How-to content shown above the shortcuts, under "How it works". */
  intro?: React.ReactNode;
}

const sectionHeadingSx = { fontWeight: 700, color: 'text.primary', mb: 2 } as const;

/**
 * Keyboard shortcut reference (opened with "?"). Shortcuts pause while typing.
 * With an intro it's the page's help: how it works first, then the keys.
 */
const ShortcutHelpDialog: React.FC<ShortcutHelpDialogProps> = ({
  open,
  onClose,
  groups = STUDIO_SHORTCUTS,
  title = 'Keyboard shortcuts',
  intro,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="shortcut-help-title">
    <DialogTitle id="shortcut-help-title" sx={{ fontWeight: 700 }}>{title}</DialogTitle>
    <DialogContent>
      {intro && (
        <Box component="section" aria-labelledby="shortcut-help-how" sx={{ mb: 4 }}>
          <Typography id="shortcut-help-how" variant="subtitle1" component="h3" sx={sectionHeadingSx}>
            How it works
          </Typography>
          {intro}
        </Box>
      )}
      {intro && (
        <Typography variant="subtitle1" component="h3" sx={{ ...sectionHeadingSx, pt: 3, borderTop: `1px solid ${brand.border}` }}>
          Keyboard shortcuts
        </Typography>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
        {groups.map((group) => (
          <Box key={group.title}>
            <Typography variant="subtitle2" component={intro ? 'h4' : 'h3'} sx={{ fontWeight: 700, color: 'text.primary', mb: 1.25 }}>
              {group.title}
            </Typography>
            <Box component="dl" sx={{ m: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {group.items.map((item) => (
                <Box key={`${group.title}-${item.label}-${item.keys.join('')}`} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                  <Box component="dt" sx={{ display: 'flex', gap: 0.5, flexShrink: 0, minWidth: 92 }}>
                    {item.keys.map((key) => <Key key={key}>{key}</Key>)}
                  </Box>
                  <Typography component="dd" variant="body2" sx={{ m: 0, color: 'text.secondary', lineHeight: 1.5 }}>
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
        Shortcuts are paused while you type in a field.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose} variant="contained">Done</Button>
    </DialogActions>
  </Dialog>
);

export default ShortcutHelpDialog;
